import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ControlsOf, FormArray, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { Subscription } from 'rxjs';
import { Entity } from 'src/app/classes/entity.class';
import { Planning } from 'src/app/classes/planning.class';
import { UserPlanning } from 'src/app/classes/user-planning';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { EntityType } from 'src/app/services/entities/entity-type.class';
import { PlanningHasService } from 'src/app/services/planning/planning-has.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { Utils } from 'src/app/utils.class';
import { entityAvailableValidator } from 'src/app/validators/entity-available.validator';
import { localEntityAvailableEquipmentValidator } from 'src/app/validators/local-entity-available-equipment.validator';

@Component({
    selector: 'app-equipment-dialog',
    templateUrl: './equipment-dialog.component.html',
    styleUrls: ['./equipment-dialog.component.scss'],
    standalone: false,
})
export class EquipmentDialogComponent implements OnInit, OnDestroy {
    saving: boolean;
    form: FormArray<ControlsOf<FormGroupPlanningEquipment>>;
    entities: Entity[];
    entitiesMap: Map<number, Entity>;
    entityTypeMap: Map<string, EntityType>;
    filteredEntities: Entity[];
    sums: { entitytypeKey: string; amount: number }[] = [];
    fcEntity = new FormControl();
    planning: Planning;
    private subscriptions = new Subscription();

    constructor(
        public dialogRef: MatDialogRef<EquipmentDialogComponent>,
        private dialog: MatDialog,
        private confirmDialog: ConfirmDialogService,
        @Inject(MAT_DIALOG_DATA)
        data: {
            planning: Planning;
        },
        private planningService: PlanningService,
        private planningHasService: PlanningHasService,
        private entitiesService: EntitiesService
    ) {
        this.planning = data.planning;

        this.subscriptions.add(
            this.dialogRef.keydownEvents().subscribe(event => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    this.close();
                }
            })
        );
    }

    ngOnInit(): void {
        this.form = new FormArray([]);

        this.subscriptions.add(
            this.entitiesService.getMap().subscribe(entitiesMap => {
                this.entitiesMap = entitiesMap;
            })
        );
        this.subscriptions.add(
            this.entitiesService.getList().subscribe(entities => {
                this.entities = entities.filter(e => !!e.entitytypes.find(e => e.visible_small) && e.use_once);
                this.filterEmployees();
            })
        );

        this.subscriptions.add(
            this.entitiesService.getTypesMap().subscribe(entitytypeMap => {
                this.entityTypeMap = entitytypeMap;
                this.planning.planning_has
                    .filter(p => entitytypeMap.get(p.entitytype_key).visible_small)
                    .forEach(planningHas => {
                        const fg = new FormGroup<ControlsOf<FormGroupPlanningEquipment>>(
                            {
                                id: new FormControl(planningHas.id),
                                entitytype_key: new FormControl(planningHas.entitytype_key),
                                entity_id: new FormControl(planningHas.entity_id),
                                planning_id: new FormControl(planningHas.planning_id),
                                begindate: new FormControl(planningHas.begindate),
                                enddate: new FormControl(planningHas.enddate),
                                conflictText: new FormControl(''),
                                comment: new FormControl(planningHas.comment),
                                updated_at: new FormControl(planningHas.updated_at),
                            },
                            [],
                            [
                                localEntityAvailableEquipmentValidator(this.planningHasService, this.entitiesMap),
                                entityAvailableValidator(this.planningHasService, this.entitiesMap),
                            ]
                        );
                        this.subscriptions.add(
                            fg.controls.begindate.valueChanges.subscribe(() => {
                                fg.controls.enddate.updateValueAndValidity();
                            })
                        );
                        this.subscriptions.add(
                            fg.controls.enddate.valueChanges.subscribe(enddate => {
                                const begindate = new Date(fg.controls.begindate.value);
                                enddate = new Date(enddate);
                                begindate.setSeconds(0);
                                const endTime = new Date(begindate);
                                endTime.setHours(enddate.getHours());
                                endTime.setMinutes(enddate.getMinutes());

                                if (endTime < begindate && enddate.getDate() !== begindate.getDate() + 1) {
                                    enddate.setDate(enddate.getDate() + 1);
                                    fg.controls.enddate.setValue(enddate, { emitEvent: false, onlySelf: true });
                                }
                                if (endTime > begindate && enddate.getDate() !== begindate.getDate()) {
                                    enddate.setDate(begindate.getDate());
                                    fg.controls.enddate.setValue(enddate, { emitEvent: false, onlySelf: true });
                                }
                            })
                        );
                        this.form.push(fg);
                    });
                this.calculateSums();
            })
        );
        setTimeout(() => {
            (document.querySelector('#close-empl') as HTMLElement).focus();
        }, 400);
    }

    filterEmployees() {
        const currentEntities = this.form.value.map(e => e.entity_id);
        this.filteredEntities = this.entities?.filter(e => currentEntities.indexOf(e.id) === -1);
    }

    calculateSums() {
        this.filterEmployees();
        this.sums = [];
        this.form.value.forEach(entity => {
            const sum = this.sums.find(p => p.entitytypeKey == entity.entitytype_key);
            if (sum) {
                sum.amount++;
            } else {
                this.sums.push({
                    entitytypeKey: entity.entitytype_key,
                    amount: 1,
                });
            }
        });
    }

    addEquipment(entityId: number) {
        const fg = new FormGroup<ControlsOf<FormGroupPlanningEquipment>>(
            {
                id: new FormControl(),
                entity_id: new FormControl(entityId),
                entitytype_key: new FormControl(this.entitiesMap.get(entityId).entitytypes[0].key),
                planning_id: new FormControl(this.planning.id),
                begindate: new FormControl(Utils.mainPlanning(this.planning).begindate, Validators.required),
                enddate: new FormControl(Utils.mainPlanning(this.planning).enddate, Validators.required),
                conflictText: new FormControl(),
                comment: new FormControl(),
                updated_at: new FormControl(),
            },
            [],
            [
                localEntityAvailableEquipmentValidator(this.planningHasService, this.entitiesMap),
                entityAvailableValidator(this.planningHasService, this.entitiesMap),
            ]
        );
        this.fcEntity.reset();
        this.form.push(fg);
        this.calculateSums();
    }

    delete(formgroup: FormGroup<ControlsOf<FormGroupPlanningEquipment>>) {
        this.confirmDialog
            .confirm({
                title: 'Verwijderen',
                text: `Weet u zeker dat u ${this.entitiesMap.get(formgroup.value.entity_id).name} wilt verwijderen van de planning?`,
                confirm: 'Ja',
                cancel: 'Nee',
            })
            .then(
                () => {
                    this.form.controls.splice(this.form.controls.indexOf(formgroup), 1);
                    this.calculateSums();
                },
                () => {}
            );
    }

    save() {
        this.saving = true;
        Utils.triggerValidationP(this.form).then(
            () => {
                this.planningService.saveMultiple(this.form.value as any as UserPlanning, this.planning.id).subscribe(
                    () => {
                        this.saving = false;
                        this.dialogRef.close();
                    },
                    () => {
                        this.saving = false;
                    }
                );
            },
            () => {
                this.saving = false;
            }
        );
    }

    close() {
        if (this.form.dirty) {
            this.confirmDialog
                .confirm({
                    title: 'Niet opgeslagen wijzigingen',
                    text: `Er zijn niet opgeslagen wijzigingen, wilt u deze alsnog opslaan?`,
                    confirm: 'Opslaan',
                    cancel: 'Niet opslaan',
                })
                .then(
                    () => {
                        this.save();
                    },
                    () => {
                        this.dialogRef.close();
                    }
                );
        } else {
            this.dialogRef.close();
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
}

export interface FormGroupPlanningEquipment {
    id: number;
    updated_at: FormControl<Date>;
    begindate: FormControl<Date>;
    enddate: FormControl<Date>;
    entitytype_key: string;
    entity_id: number;
    planning_id: number;
    comment: string;
    conflictText: string;
}
